import { type InputHTMLAttributes, type ReactNode, type Ref, useId } from "react";
import { controlClass, Field, fieldState } from "./Field";

/** Single-line input with the mono label above and hint or error below. */
export function TextInput({
  label,
  hint,
  error,
  id: givenId,
  className = "",
  mono = false,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Mono text for codes and ids. */
  mono?: boolean;
  /** Handed straight to the input, so a screen can focus the field on mount. */
  ref?: Ref<HTMLInputElement>;
}) {
  const generated = useId();
  const id = givenId ?? generated;
  const { invalid, describedBy } = fieldState(id, error, hint);
  return (
    <Field
      id={id}
      className={className}
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(invalid ? { error } : {})}
    >
      <input
        {...rest}
        id={id}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-component="TextInput"
        className={controlClass(
          invalid,
          `h-control ${mono ? "font-mono" : ""}`,
          mono ? "text-data" : "text-ui",
        )}
      />
    </Field>
  );
}
